import { ContractTenant, FileTypeEnum, TenantSeniorityStatusEnum } from 'api/quote';
import { ReferentialKey } from 'api/referential';
import {
  DocumentSection,
  GenericDocumentsManager
} from 'components/domain/Document/GenericDocumentManager';
import { NsKey } from 'enums/NsKey';
import { selectReferentialDataByKey } from 'features/referentials/selectors/selectReferentialDataByKey';
import { useRefetchQuoteData } from '../../hooks/useRefetchQuoteData';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { FC, useCallback, useMemo } from 'react';
import { getCivilityIcon } from 'shared/utils/civility';
import { useSelector } from 'react-redux';
import { filesAPI, tenantsAPI } from 'services/swagger-api';

interface TenantsDocumentsManagerProps {
  tenants: ContractTenant[];
  refresh: () => void;
  /** Titre de section — chaîne vide pour le masquer (mode parc : le bien porte déjà le titre) */
  title?: string;
}

export const TenantsDocumentsManager: FC<TenantsDocumentsManagerProps> = ({
  tenants,
  refresh,
  title
}) => {
  const { t } = useDynamicTranslation([NsKey.RealEstate, NsKey.Common]);
  const refetchQuoteData = useRefetchQuoteData();
  const civilityReferential = useSelector(
    selectReferentialDataByKey(ReferentialKey.Civility)
  );

  const getGenderIcon = useCallback(
    (civilityId: string) => {
      const ICON_CLASS = 'w-5 text-primary';
      const civilityRef = civilityReferential.find(
        ref => ref.id === civilityId
      );
      const Icon = getCivilityIcon(civilityRef?.code);
      return <Icon className={ICON_CLASS} />;
    },
    [civilityReferential]
  );

  const sections: DocumentSection[] = useMemo(() => {
    return tenants.flatMap(tenant => {
      // Locataire en place : aucun justificatif exigé, la déclaration « à jour de ses loyers »
      // (validée à l'étape dossier location) fait foi — l'état affiché l'explique.
      const isInPlace = tenant.seniorityStatus === TenantSeniorityStatusEnum.InPlace;

      const tenantSection: DocumentSection = {
        id: tenant.id,
        title: tenant.fullName,
        icon: tenant.civilityId ? getGenderIcon(tenant.civilityId) : undefined,
        documents:
          tenant.documents?.map(doc => ({
            fileType: doc.fileType,
            name: doc.name,
            storedFileId: doc.storedFileId,
            allowMultiple: doc.allowMultiple,
            files: doc.files
          })) || [],
        isEligible: tenant.isEligible,
        isCompleted: tenant.isCompleted,
        completedLabel: isInPlace ? t('TENANT.LABELS.IN_PLACE_UP_TO_DATE') : undefined,
        emptyMessage: isInPlace ? t('TENANT.LABELS.IN_PLACE_NO_DOCUMENTS') : undefined
      };

      const guarantorSection: DocumentSection | null = tenant.guarantor
        ? {
          id: `${tenant.id}-guarantor`,
          title: `${tenant.fullName} - Garant`,
          documents:
            tenant.guarantor.documents?.map(doc => ({
              fileType: doc.fileType,
              name: doc.name,
              storedFileId: doc.storedFileId,
              allowMultiple: doc.allowMultiple,
              files: doc.files
            })) || [],
          isEligible: tenant.isEligible,
          isCompleted:
            tenant.guarantor.documents?.every(doc => doc.storedFileId) ||
            false
        }
        : null;

      return guarantorSection ? [tenantSection, guarantorSection] : [tenantSection];
    });
  }, [tenants, getGenderIcon, t]);

  const handleUpload = useCallback(
    async (sectionId: string, file: File, fileType: FileTypeEnum) => {
      const response = await filesAPI.uploadFileByType({ fileType, file });

      if (response.isSuccess && response.id) {
        const isGuarantor = sectionId.includes('-guarantor');
        const tenantId = isGuarantor
          ? sectionId.replace('-guarantor', '')
          : sectionId;
        const tenant = tenants.find(t => t.id === tenantId);
        const guarantorId = isGuarantor ? tenant?.guarantor?.id : undefined;

        await tenantsAPI.createTenantDocument({
          tenantId,
          storedFileIdRequest: { storedFileId: response.id },
          garantId: guarantorId
        });

        await refetchQuoteData();
      }
    },
    [tenants, refetchQuoteData]
  );

  const handleRemove = useCallback(
    async (sectionId: string, storedFileId: string) => {
      const isGuarantor = sectionId.includes('-guarantor');
      const tenantId = isGuarantor
        ? sectionId.replace('-guarantor', '')
        : sectionId;

      const tenant = tenants.find(t => t.id === tenantId);
      const guarantorId = isGuarantor ? tenant?.guarantor?.id : undefined;

      await tenantsAPI.deleteTenantDocument({
        tenantId,
        storedFileId,
        garantId: guarantorId
      });

      await refetchQuoteData();
    },
    [tenants, refetchQuoteData]
  );

  return (
    <GenericDocumentsManager
      sections={sections}
      onUpload={handleUpload}
      onRemove={handleRemove}
      disabledSectionLabel={t('TENANT.LABELS.NOT_ELIGIBLE')}
      title={title ?? t('REAL_ESTATE.LABELS.TENANTS')}
      emptyMessage="Aucun document requis"
    />
  );
};
